import React, { useMemo } from 'react';
import BaseSelectBox from 'components/atoms/base-select-box';
import { actionTypesDataSource } from 'infrastructure/data-sources/action-types';

import type { ModuleType } from 'infrastructure/enums';
import type { Prettify } from 'infrastructure/types';
import type { IActionTypesItem } from 'infrastructure/data-sources/action-types';
import type {
  IBaseSelectBoxOption,
  IBaseSelectBoxProps,
} from 'components/atoms/base-select-box/types';
import type { ActionTypes } from 'infrastructure/enums/action-types';

type TValue<TMode> = TMode extends 'multiple' | 'tags'
  ? ActionTypes[]
  : ActionTypes;

type Options = Prettify<
  IBaseSelectBoxOption<IActionTypesItem['id'], IActionTypesItem>[]
>;

type TOption<TMode> = TMode extends 'multiple' | 'tags'
  ? Options
  : Options[number];

interface IActionTypesSelectBoxProps<
  TMode extends 'multiple' | 'tags' | undefined = undefined,
> extends Omit<
    IBaseSelectBoxProps<TValue<TMode>>,
    'options' | 'mode' | 'onChange'
  > {
  mode?: TMode;
  withNone?: boolean;
  showAll?: boolean;
  onChange?: (value: TValue<TMode>, option: TOption<TMode>) => void;
  excludeTypes?: ActionTypes[];
  filterByModule?: ModuleType;
}

const ActionTypesSelectBox = <
  TMode extends 'multiple' | 'tags' | undefined = undefined,
>(
  props: IActionTypesSelectBoxProps<TMode>,
) => {
  const { withNone, showAll, onChange, excludeTypes, filterByModule } = props;
  const dataSource = useMemo(() => {
    const items = actionTypesDataSource(withNone, showAll, excludeTypes);

    if (filterByModule)
      return items.filter((el) => el.modules.includes(filterByModule));

    return items;
  }, [withNone, showAll, excludeTypes, filterByModule]);

  const options: Options = useMemo(
    () =>
      dataSource.map((el) => ({
        value: el.id,
        label: el.name,
        record: el,
      })),
    [dataSource],
  );

  return (
    <BaseSelectBox
      options={options}
      // @ts-ignore
      onChange={onChange}
      {...props}
    />
  );
};

export default ActionTypesSelectBox;
